import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import Modal from "@mui/material/Modal";

import HeaderOrg from "../Headers/HeaderOrg";
import Loader from "../../Components/Loader/Loader";
import Help from "../../Components/Help/Help";
import Button from "../../Components/Button/Button";
import ToggleSwitch from "../../Components/ToggleSwitch/ToggleSwitch";
import closeIcon from "../../Assets/close-icon.svg";
import { ShowToastMessage } from "../../helper/ShowToastMessage";
import PublishConfirm, { publishAsk } from "./PublishConfirm";
import { useJobDevice } from "../../device/DeviceContext";
import useAutoRefresh from "./useAutoRefresh";
import { useMaterialSymbols, Msr } from "../OireachtasPanel/symbols";
import { eventDayKey, scheduledToday } from "./tabulationFlow";
import { ALL, chipsOf, inChip, sortRows, todayRows, roundCounts, stepsOf, headlineOf, primaryActionOf, summaryOf } from "./statusModel";
import "../OireachtasPanel/oireachtas.css";
import "../OireachtasPanel/organizerOireachtas.css";
import "./oireachtasTabulation.css";
import "./oireachtasStatus.css";
import { serviceConsumer } from "../../network/ServiceConsumer";
import {
  getTabulationStatus,
  publishRecall,
  publishResults,
  setResultsMasterGate,
  clearScoreColumn,
} from "../../network/oireachtasEventDay";

// Refreshes every 10 seconds while on screen, and on coming back to the tab
// (Devin 2026-10-22, like the feis Result Status screen; every 10 seconds
// since the ECRO dress rehearsal, Devin 2026-11-02): other tabulators enter,
// sign and publish from other laptops.
const REFRESH_MS = 10 * 1000;
const SCHEDULE_MS = 5 * 60 * 1000;
const timeLabel = (d) => (d ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "");

// Status — the whole event's tabulation picture on one screen (Devin's IA,
// 2026-08-17): every competition's progress through entry → judges → sign-off,
// with the recall/result publish toggles activating as each competition's
// required data is confirmed (gate = adjudicator sign-off, enforced
// server-side too), and the event-level master gate in the header. Drill-in
// links jump to View Scores / the per-comp detail. Absorbs the old Results &
// Recalls entry point as the Tabulate section's overview.
const OireachtasStatus = () => {
  const [event, setEvent] = useState(null);
  const [rows, setRows] = useState([]);
  useMaterialSymbols();
  const [eventDoc, setEventDoc] = useState(null); // full event (name, time zone)
  // today's competitions from the schedule (null = none today: list all), as
  // View Scores does; the tabulator can lift the filter; one type chip
  // the stage schedule; today's competitions are worked out from it on every
  // render, so a screen left open overnight moves to the next day, and it is
  // read again every 5 minutes (code review a33172ab R1-F1)
  const [schedule, setSchedule] = useState(null);
  const scheduleAt = useRef(0);
  const [showAll, setShowAll] = useState(false);
  const [chip, setChip] = useState(ALL);
  const [loading, setLoading] = useState(false);
  const [busyComp, setBusyComp] = useState(""); // compId with a toggle in flight
  const [cellModal, setCellModal] = useState(null); // { row, roundRow } — round-detail modal
  const [pendingClear, setPendingClear] = useState(null); // { row, round, slot } awaiting confirm
  const [clearing, setClearing] = useState(false);
  const [gateBusy, setGateBusy] = useState(false); // the family-results switch in flight
  // every publish action asks first (Devin 2026-10-22):
  // { title, body, label, run } while a switch waits for its answer
  const [ask, setAsk] = useState(null);
  // On an event-day device (card 540f21a4) the job decides what shows:
  // publishing and unpublishing (each its own function), clearing a sheet,
  // and the screens it links to. The family-results switch is never a device's.
  const { device: jobDevice, can } = useJobDevice();
  const canToggle = (kind, published) =>
    can(`ox.${kind}.${published ? "unpublish" : "publish"}`);
  const canScores = can("ox.scores.enter");
  // an open Clear sheet confirmation closes when score entry is unticked
  useEffect(() => {
    if (!canScores) setPendingClear(null);
  }, [canScores]);
  const canDetail = can("ox.results.check", "ox.roundawards", "ox.results.publish", "ox.results.unpublish", "ox.recall.publish", "ox.recall.unpublish");
  const [loadedAt, setLoadedAt] = useState(null);
  const [refreshError, setRefreshError] = useState(false);
  const requestSeq = useRef(0); // only the newest load is shown
  const pausedRef = useRef(false); // a dialog open or an action in flight
  const navigate = useNavigate();
  const location = useLocation();
  const eventId = new URLSearchParams(location.search).get("id");

  // background: the automatic refresh — a failure shows on the "Updated"
  // line instead of a toast
  const load = async (withSpinner, background = false) => {
    const seq = ++requestSeq.current;
    try {
      if (withSpinner) setLoading(true);
      const res = await getTabulationStatus(eventId);
      if (seq !== requestSeq.current) return;
      setEvent(res.event);
      setRows(res.rows || []);
      const now = new Date();
      setLoadedAt(now);
      setRefreshError(false);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      if (background) setRefreshError(true);
      else ShowToastMessage("error", e?.messages || "Could not load tabulation status");
    } finally {
      if (withSpinner) setLoading(false);
    }
  };

  pausedRef.current = !!(cellModal || pendingClear || busyComp || clearing || gateBusy || ask);
  // an automatic read still waiting is never overlapped by the next one, so a
  // read slower than the interval still lands (plan review 7631d72b R1-F3)
  // a failed re-read keeps the schedule already shown; none = list everything
  const readSchedule = async () => {
    scheduleAt.current = Date.now();
    const res = await serviceConsumer("GET", `${process.env.REACT_APP_BASE_URL}/oireachtas/${eventId}/schedule`).catch(
      () => undefined
    );
    if (res !== undefined) setSchedule(res);
  };
  useAutoRefresh(
    () => {
      if (Date.now() - scheduleAt.current > SCHEDULE_MS) readSchedule();
      return load(false, true);
    },
    { everyMs: REFRESH_MS, active: !!eventId, paused: pausedRef.current }
  );

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [eventRes] = await Promise.all([
          serviceConsumer("GET", `${process.env.REACT_APP_BASE_URL}/oireachtas/${eventId}`),
          load(false),
          readSchedule(),
        ]);
        setEventDoc(eventRes.event);
      } catch (e) {
        // status load reports its own errors
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // ---- publish toggles (server re-enforces the sign-off gate and the check) ----
  const askRecall = (row) =>
    setAsk({
      fn: row.recallPublished ? "ox.recall.unpublish" : "ox.recall.publish",
      ...publishAsk("recall", row.competition.code, !row.recallPublished),
      run: () => toggleRecall(row),
    });
  const askResults = (row) =>
    setAsk({
      fn: row.resultsPublished ? "ox.results.unpublish" : "ox.results.publish",
      ...publishAsk("results", row.competition.code, !row.resultsPublished),
      run: () => toggleResults(row),
    });
  const askMasterGate = () => setAsk({ ...publishAsk("families", "", !event.resultsPublished), run: toggleMasterGate });
  const answerAsk = () => {
    const run = ask && ask.run;
    setAsk(null);
    // the job is re-read every minute: a confirmation opened before a
    // function was unticked doesn't send it (MC review c87cf2d7 R3-F2)
    if (ask && ask.fn && !can(ask.fn)) return;
    if (run) run();
  };
  const askFn = ask && ask.fn;
  const askAllowed = !askFn || can(askFn);
  useEffect(() => {
    if (!askAllowed) setAsk(null);
  }, [askAllowed]);

  const toggleRecall = async (row) => {
    const compId = row.competition._id;
    if (!row.recallPublished && !row.gate.recallReady) {
      ShowToastMessage("error", "Recall needs every judge signed off through round 2");
      return;
    }
    try {
      setBusyComp(compId);
      await publishRecall(eventId, compId, row.recallPublished);
      await load(false);
      ShowToastMessage("success", row.recallPublished ? "Recall unpublished" : "Recall published");
    } catch (e) {
      ShowToastMessage("error", e?.messages || "Could not update the recall");
    } finally {
      setBusyComp("");
    }
  };

  const toggleResults = async (row) => {
    const compId = row.competition._id;
    if (!row.resultsPublished && !row.gate.resultsReady) {
      ShowToastMessage("error", "Results need every judge signed off on every round");
      return;
    }
    try {
      setBusyComp(compId);
      await publishResults(eventId, compId, row.resultsPublished);
      await load(false);
      ShowToastMessage("success", row.resultsPublished ? "Results unpublished" : "Results published");
    } catch (e) {
      ShowToastMessage("error", e?.messages || "Could not update the results");
    } finally {
      setBusyComp("");
    }
  };

  const toggleMasterGate = async () => {
    try {
      setGateBusy(true);
      requestSeq.current += 1; // a refresh already on its way read the old switch
      const res = await setResultsMasterGate(eventId, !event.resultsPublished);
      requestSeq.current += 1;
      setEvent((ev) => ({ ...ev, resultsPublished: res.resultsPublished }));
    } catch (e) {
      ShowToastMessage("error", e?.messages || "Could not update the master gate");
    } finally {
      setGateBusy(false);
    }
  };

  // ---- clear a judge column (round-detail modal -> confirm -> DELETE) ----
  const doClear = async () => {
    const { row, round, slot } = pendingClear;
    setPendingClear(null);
    // the job is re-read every minute (MC review c87cf2d7 R4-F1)
    if (!canScores) return;
    try {
      setClearing(true);
      const res = await clearScoreColumn(eventId, row.competition._id, round, slot);
      ShowToastMessage("success", res.message || "Sheet cleared");
      setCellModal(null);
      await load(false);
    } catch (e) {
      ShowToastMessage("error", e?.messages || "Could not clear the sheet");
    } finally {
      setClearing(false);
    }
  };

  // ---- the list: today's competitions, a type chip, syllabus order ----
  const sorted = useMemo(() => sortRows(rows), [rows]);
  const todayIds = scheduledToday(schedule?.schedule, eventDayKey(schedule?.event?.timeZone || eventDoc?.timeZone));
  const dayRows = todayRows(sorted, todayIds, showAll);
  const chips = chipsOf(dayRows);
  const chipOn = chips.includes(chip) ? chip : ALL;
  const shown = dayRows.filter((r) => inChip(r, chipOn));
  const summary = summaryOf(shown);
  const perms = { canPublish: (kind) => canToggle(kind, false), canScores, canDetail };
  const openScores = (c) => navigate(`/oireachtas/tabulation?id=${eventId}&comp=${c._id}`);
  const openDetail = (c) => navigate(`/oireachtas/results?id=${eventId}&comp=${c._id}`);
  const STEP_ICON = { done: "check", current: "more_horiz", waiting: "" };

  const card = (row) => {
    const c = row.competition;
    const busy = busyComp === c._id;
    const head = headlineOf(row);
    const action = primaryActionOf(row, perms);
    const unpublishRecall = row.recallPublished && canToggle("recall", true);
    const unpublishResults = row.resultsPublished && canToggle("results", true);
    return (
      <section key={c._id} className={`card st-card ${head.tone}`} aria-label={`${c.code} ${c.name}`}>
        <div className="st-head">
          <div className="st-title">
            <b className="st-code">{c.code}</b>
            <span className="st-name">{c.name}</span>
          </div>
          <span className={`op-pill st-pill ${head.tone}`}>{head.short}</span>
        </div>
        {/* an admin correction after publishing: public page still shows the old version */}
        {(row.corrected?.results || row.corrected?.recall) && (
          <div className="st-warn">Corrected after publishing — publish again on View Scores</div>
        )}
        <ol className="st-track">
          {stepsOf(row).map((s) => {
            const roundRow = s.round ? row.roundRows.find((r) => r.round === s.round) : null;
            const opens = !!roundRow && roundCounts(row, s.round).started;
            const inner = (
              <>
                <span className="st-dot" aria-hidden="true">
                  {STEP_ICON[s.state] ? <Msr>{STEP_ICON[s.state]}</Msr> : null}
                </span>
                <span className="st-label">{s.label}</span>
                {s.detail && <span className="st-detail">{s.detail}</span>}
              </>
            );
            return (
              <li key={s.key} className={`st-step ${s.state}${s.alert ? " attn" : ""}`}>
                {opens ? (
                  <button
                    type="button"
                    className="st-step-b"
                    title="Each judge's sheet — and Clear sheet"
                    onClick={() => setCellModal({ row, roundRow })}
                  >
                    {inner}
                  </button>
                ) : (
                  <div className="st-step-b">{inner}</div>
                )}
              </li>
            );
          })}
        </ol>
        <div className="st-line">{head.line}</div>
        <div className="st-actions">
          {action === "publish-recall" && (
            <button type="button" className="op-btn go" disabled={busy} onClick={() => askRecall(row)}>
              <Msr>campaign</Msr> Publish recall
            </button>
          )}
          {action === "publish-results" && (
            <button type="button" className="op-btn go" disabled={busy} onClick={() => askResults(row)}>
              <Msr>campaign</Msr> Publish results
            </button>
          )}
          {action === "open-scores" && (
            <button type="button" className="op-btn" disabled={busy} onClick={() => openScores(c)}>
              <Msr>edit_note</Msr> Open in View Scores
            </button>
          )}
          {action === "open-detail" && (
            <button type="button" className="op-btn" disabled={busy} onClick={() => openDetail(c)}>
              <Msr>leaderboard</Msr> Detail
            </button>
          )}
          <span className="st-links">
            {canScores && action !== "open-scores" && (
              <button type="button" className="st-link" disabled={busy} onClick={() => openScores(c)}>
                Scores
              </button>
            )}
            {canDetail && action !== "open-detail" && (
              <button type="button" className="st-link" disabled={busy} onClick={() => openDetail(c)}>
                Detail
              </button>
            )}
            {unpublishRecall && (
              <button type="button" className="st-link" disabled={busy} onClick={() => askRecall(row)}>
                Unpublish recall
              </button>
            )}
            {unpublishResults && (
              <button type="button" className="st-link" disabled={busy} onClick={() => askResults(row)}>
                Unpublish results
              </button>
            )}
          </span>
        </div>
      </section>
    );
  };

  return (
    <>
      {loading && <Loader />}
      <HeaderOrg />
      <div className="oir op-page st-page">
        <div className="wrap">
          <button type="button" className="op-back" onClick={() => navigate(`/oireachtas/manage?id=${eventId}`)}>
            <Msr>arrow_back</Msr> {eventDoc?.name || "Event"}
          </button>
          <div className="ph">
            <div>
              <h1>Status</h1>
              <div className="sub">
                <span>
                  Where every competition is: its rounds, the recall and the results. Publish here once a
                  tabulator has checked it in View Scores.
                </span>
              </div>
              {loadedAt && (
                <div className={`tb-updated${refreshError ? " failed" : ""}`} role="status">
                  {refreshError
                    ? `Couldn't refresh — showing ${timeLabel(loadedAt)}`
                    : `Updated ${timeLabel(loadedAt)} · refreshes every 10 seconds`}
                </div>
              )}
            </div>
          </div>

          {!loading && (
            <>
              {event && !jobDevice && (
                <div className="card st-family">
                  <ToggleSwitch checked={!!event.resultsPublished} onChange={askMasterGate} disabled={gateBusy} />
                  <div>
                    <b>Family results (parent view)</b>
                    <div className="op-muted">
                      {event.resultsPublished
                        ? "Families can see their dancers' published results"
                        : "Hidden from families until switched on — published comps still show on the public results page"}
                    </div>
                  </div>
                </div>
              )}

              <div className="st-filters">
                {todayIds && (
                  <div className="st-day">
                    <b>{showAll ? "Every competition" : "Today's competitions"}</b>
                    <button type="button" className="tb-showall" onClick={() => setShowAll(!showAll)}>
                      {showAll ? "Only today's competitions" : "Show every competition"}
                    </button>
                  </div>
                )}
                {chips.length > 2 && (
                  <div className="st-chips" role="group" aria-label="Competition type">
                    {chips.map((ch) => (
                      <button
                        key={ch}
                        type="button"
                        className={`op-chip${ch === chipOn ? " on" : ""}`}
                        aria-pressed={ch === chipOn}
                        onClick={() => setChip(ch)}
                      >
                        {ch}
                      </button>
                    ))}
                  </div>
                )}
                <div className="st-summary">
                  {summary.total} competition{summary.total === 1 ? "" : "s"}
                  {summary.inProgress ? ` · ${summary.inProgress} in progress` : ""}
                  {summary.signOff ? ` · ${summary.signOff} awaiting sign-off` : ""}
                  {summary.ready ? ` · ${summary.ready} ready to publish` : ""}
                  {summary.published ? ` · ${summary.published} published` : ""}
                  {summary.notDrawn ? ` · ${summary.notDrawn} not drawn` : ""}
                </div>
              </div>

              <div className="st-list">{shown.map(card)}</div>
              {shown.length === 0 && <div className="op-empty">No competitions to show.</div>}
            </>
          )}
        </div>
      </div>
      {/* ---- round-detail modal: per-judge state + clear-sheet ---- */}
      <PublishConfirm ask={ask} onConfirm={answerAsk} onCancel={() => setAsk(null)} />
      <Modal open={!!cellModal} onClose={() => !clearing && setCellModal(null)}>
        <div
          style={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            background: "#FFFFFF", borderRadius: "8px", padding: "24px",
            width: "min(440px, 92vw)", outline: "none",
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <b>
              {cellModal?.row.competition.code} — Round {cellModal?.roundRow.round}
            </b>
            <img
              src={closeIcon}
              alt="close"
              style={{ cursor: "pointer" }}
              onClick={() => !clearing && setCellModal(null)}
            />
          </div>
          <div className="grey-42 mb-3" style={{ fontSize: "12px" }}>
            Clearing wipes every score in that judge's column for this round (a sheet typed into
            the wrong column). A signed-off sheet is invalidated with audit. Blocked while the
            competition is published. To fix single cells, use Scores instead.
          </div>
          {cellModal?.roundRow.checkIn && (
            <div className="grey-42 mb-2" style={{ fontSize: "12px" }}>
              Check-in:{" "}
              {cellModal.roundRow.checkIn.taken
                ? `${cellModal.roundRow.checkIn.checkedIn} checked in · ${cellModal.roundRow.checkIn.notComing} no show`
                : "not taken"}
            </div>
          )}
          {cellModal?.roundRow.slots.map((s) => (
            <div key={s.slot} className="d-flex align-items-center justify-content-between my-2">
              <span style={{ fontSize: "13px" }}>
                {s.judge ? `${s.judge} (${s.slot})` : `Judge ${s.slot} — no judge yet`}
                <span className="grey-42" style={{ marginLeft: "10px" }}>
                  {s.entered}/{s.total} entered ·{" "}
                  {s.signOff === "missing" ? "not signed" : s.signOff.replace("_", " ")}
                </span>
              </span>
              {canScores && (
              <Button
                className="text-btn"
                disabled={clearing || (typeof s.stored === "number" ? s.stored : s.entered) === 0}
                onClick={() =>
                  setPendingClear({ row: cellModal.row, round: cellModal.roundRow.round, slot: s.slot, judge: s.judge })
                }
              >
                Clear sheet
              </Button>
              )}
            </div>
          ))}
        </div>
      </Modal>

      {/* clear-sheet confirm */}
      <PublishConfirm
        ask={
          pendingClear && {
            title: `Clear ${pendingClear.judge || `judge ${pendingClear.slot}`}'s round ${pendingClear.round} sheet for ${pendingClear.row.competition.code}?`,
            body: "Every score on it is deleted, and the sheet must be entered and signed again.",
            label: "Clear sheet",
          }
        }
        onConfirm={doClear}
        onCancel={() => setPendingClear(null)}
      />
      <Help />
      <ToastContainer />
    </>
  );
};

export default OireachtasStatus;
